import { Component, HostListener, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { CanDeactivateComponent } from '../survey-builder/survey-builder.guard';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { ApiEmailTranslationsService, ApiPutEmailTranslationsResponse, Translation } from '../../../../shared/services/api-emailtranslations.service';
import { ApiSurveyTranslationsGlobalService } from '../../../../shared/services/api-surveytranslationsglobal.service';
import { SupportedLanguage } from '../../../../shared/models/supportedLanguage.interface';

type EmailTranslationCategory = 'survey' | 'global';

@Component({
  selector: 'app-email-translations',
  templateUrl: './email-translations.component.html',
  styleUrls: ['./email-translations.component.css'],
  imports: [FormsModule, SurveyHeaderComponent, BreadcrumbComponent, PageLoaderComponent]
})
export class EmailTranslationsComponent implements OnInit, CanDeactivateComponent {
  readonly categories: { key: EmailTranslationCategory; label: string }[] = [
    { key: 'survey', label: 'Survey' },
    { key: 'global', label: 'Global' }
  ];

  organizationId = '';
  supportedLanguages: Array<{ lang: string } & SupportedLanguage> = [];
  selectedLanguage = '';
  previousLanguage: string | null = null;

  translations: Record<EmailTranslationCategory, Translation[]> = { survey: [], global: [] };
  // Survey-category translationIDs look like "<surveyOrAlertId>:[Tag]" — the list is filtered to one id at a time.
  selectedSurveyGroup = '';
  // Global-category filter: case-insensitive substring match on translationID.
  globalFilter = '';
  // Last-loaded textValue per translation id — the baseline for change tracking.
  private originalSnapshot = new Map<string, string>();
  // Ids the backend rejected on the last save, highlighted until the next load.
  failedIds = new Set<string>();

  loading = signal(false);
  loadingTranslations = signal(false);
  error = signal<string | null>(null);

  saving = signal(false);
  saveError = signal<string | null>(null);
  showSaveConfirm = signal(false);
  showSaveResult = signal<{ success: number; failed: { id: string; reason: string }[] } | null>(null);
  showDiscardConfirm = signal(false);
  showLanguageChangeConfirm = signal(false);
  showLeaveConfirm = signal(false);
  private leaveResolve: ((allow: boolean) => void) | null = null;

  readonly canEdit = computed(() => this.userStore.isAdmin());

  constructor(
    private router: Router,
    private organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private emailTranslationsService: ApiEmailTranslationsService,
    private globalTranslationsService: ApiSurveyTranslationsGlobalService) {}

  ngOnInit() {
    const organizationId = this.organizationStore.selectedOrganizationId();
    if (!organizationId) {
      this.router.navigate(['/organizations']);
      return;
    }
    this.organizationId = organizationId;
    this.loadSupportedLanguages();
  }

  // The email translations endpoint doesn't return the organization's languages, so they come from
  // the global translations endpoint (the same source the Global Translations page uses).
  private loadSupportedLanguages(): void {
    this.loading.set(true);
    this.error.set(null);

    this.globalTranslationsService.getSurveyTranslationsGlobal(this.organizationId, null).subscribe({
      next: (response) => {
        this.supportedLanguages = Object.entries(response.supportedLanguages).map(([key, value]) => ({
          lang: key,
          ...value
        }));
        this.loading.set(false);

        if (this.supportedLanguages.length === 0) {
          this.error.set('This organization has no supported languages.');
          return;
        }

        const languages = this.supportedLanguages.map(l => l.lang);
        this.selectedLanguage = languages.includes('en') ? 'en' : languages[0];
        this.previousLanguage = this.selectedLanguage;
        this.loadTranslations();
      },
      error: (err) => {
        this.error.set('Failed to load supported languages. | ' + (err?.message ?? err));
        this.loading.set(false);
      }
    });
  }

  private loadTranslations(): void {
    this.loadingTranslations.set(true);
    this.error.set(null);
    this.failedIds.clear();

    this.emailTranslationsService.getTranslations(this.organizationId, this.selectedLanguage).subscribe({
      next: (response) => {
        const byLabel = (a: Translation, b: Translation) => a.translationID.localeCompare(b.translationID);
        this.translations = {
          survey: [...(response.survey ?? [])].sort(byLabel),
          global: [...(response.global ?? [])].sort(byLabel)
        };
        this.takeSnapshot();
        // Keep the selected survey/alert across language switches when it still exists.
        const groupKeys = this.surveyGroups.map(g => g.key);
        if (!groupKeys.includes(this.selectedSurveyGroup)) {
          this.selectedSurveyGroup = groupKeys[0] ?? '';
        }
        this.loadingTranslations.set(false);
      },
      error: (err) => {
        this.translations = { survey: [], global: [] };
        this.takeSnapshot();
        this.error.set('Failed to load email translations. | ' + (err?.message ?? err));
        this.loadingTranslations.set(false);
      }
    });
  }

  // ── Survey/alert grouping ────────────────────────────────────────────────

  // The id before the first ':'; translations without one fall into an "Other" group ('').
  private surveyGroupOf(translation: Translation): string {
    const separator = translation.translationID.indexOf(':');
    return separator > 0 ? translation.translationID.substring(0, separator) : '';
  }

  get surveyGroups(): { key: string; label: string; count: number; changed: number }[] {
    const groups = new Map<string, { count: number; changed: number }>();
    for (const t of this.translations.survey) {
      const key = this.surveyGroupOf(t);
      const group = groups.get(key) ?? { count: 0, changed: 0 };
      group.count++;
      if (this.isChanged(t)) group.changed++;
      groups.set(key, group);
    }

    const surveyNames = new Map(this.organizationStore.selectedOrganizationSurveys().map(s => [s.id, s.name]));
    return Array.from(groups.entries())
      .map(([key, g]) => {
        const name = surveyNames.get(key);
        const label = !key ? 'Other' : name ? `${name} (${key})` : key;
        return { key, label, ...g };
      })
      // "Other" last, the rest alphabetically by label.
      .sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : a.label.localeCompare(b.label)));
  }

  itemsFor(category: EmailTranslationCategory): Translation[] {
    if (category === 'global') {
      const filter = this.globalFilter.trim().toLowerCase();
      if (!filter) return this.translations.global;
      return this.translations.global.filter(t => t.translationID.toLowerCase().includes(filter));
    }
    return this.translations.survey.filter(t => this.surveyGroupOf(t) === this.selectedSurveyGroup);
  }

  // ── Change tracking ──────────────────────────────────────────────────────

  private get allTranslations(): Translation[] {
    return [...this.translations.survey, ...this.translations.global];
  }

  private takeSnapshot(): void {
    this.originalSnapshot.clear();
    for (const t of this.allTranslations) {
      this.originalSnapshot.set(t.id, t.textValue ?? '');
    }
  }

  isChanged(translation: Translation): boolean {
    return (translation.textValue ?? '') !== this.originalSnapshot.get(translation.id);
  }

  private get changedTranslations(): Translation[] {
    return this.allTranslations.filter(t => this.isChanged(t));
  }

  get pendingChangesCount(): number {
    return this.changedTranslations.length;
  }

  // ── Language ─────────────────────────────────────────────────────────────

  requestLanguageChange(): void {
    if (this.pendingChangesCount === 0) {
      this.confirmLanguageChange();
      return;
    }
    this.showLanguageChangeConfirm.set(true);
  }

  confirmLanguageChange(): void {
    this.previousLanguage = this.selectedLanguage;
    this.showLanguageChangeConfirm.set(false);
    this.loadTranslations();
  }

  cancelLanguageChange(): void {
    this.selectedLanguage = this.previousLanguage ?? this.selectedLanguage;
    this.showLanguageChangeConfirm.set(false);
  }

  // ── Save ─────────────────────────────────────────────────────────────────

  requestSave(): void {
    if (!this.canEdit() || this.pendingChangesCount === 0) return;
    this.saveError.set(null);
    this.showSaveConfirm.set(true);
  }

  cancelSave(): void {
    if (this.saving()) return;
    this.saveError.set(null);
    this.showSaveConfirm.set(false);
  }

  save(): void {
    if (!this.canEdit()) return;
    const changed = this.changedTranslations.map(t => ({ id: t.id, textValue: t.textValue ?? '' }));
    if (changed.length === 0) return;

    this.saving.set(true);
    this.saveError.set(null);
    this.emailTranslationsService.updateTranslations(this.organizationId, changed).subscribe({
      next: (response: ApiPutEmailTranslationsResponse) => {
        this.saving.set(false);
        this.showSaveConfirm.set(false);
        this.showSaveResult.set({ success: response.applied?.length ?? 0, failed: response.failed ?? [] });

        // Accept what was applied as the new baseline; keep failed edits in place so they can be retried.
        for (const id of response.applied ?? []) {
          const t = this.allTranslations.find(x => x.id === id);
          if (t) this.originalSnapshot.set(id, t.textValue ?? '');
        }
        this.failedIds = new Set((response.failed ?? []).map(f => f.id));
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(err?.message ?? 'Failed to save email translations.');
      }
    });
  }

  closeSaveResult(): void {
    this.showSaveResult.set(null);
  }

  labelFor(id: string): string {
    return this.allTranslations.find(t => t.id === id)?.translationID ?? id;
  }

  // ── Discard ──────────────────────────────────────────────────────────────

  requestDiscard(): void {
    if (!this.canEdit()) return;
    this.showDiscardConfirm.set(true);
  }

  cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  discard(): void {
    if (!this.canEdit()) return;
    for (const t of this.allTranslations) {
      t.textValue = this.originalSnapshot.get(t.id) ?? t.textValue;
    }
    this.failedIds.clear();
    this.showDiscardConfirm.set(false);
  }

  // ── Leave guard ──────────────────────────────────────────────────────────

  setLeaveResolve(fn: (allow: boolean) => void): void {
    this.leaveResolve = fn;
  }

  confirmLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(true);
    this.leaveResolve = null;
  }

  cancelLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(false);
    this.leaveResolve = null;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.pendingChangesCount > 0) {
      event.preventDefault();
    }
  }
}
